import React, { FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Close, OpenInNew, Search } from '@mui/icons-material'
import { IconButton, TextField, TextFieldProps, debounce } from '@mui/material'

import './SearchInputWithDebounce.scss'

type SearchInputWithDebounceProps = TextFieldProps & {
  fieldText: string
  onDebounce: (searchText: string) => void
  onFocus?: (event: FocusEvent<any>) => void
  onBlur?: (event: FocusEvent<any>) => void
  onOpenGameSearchDialog?: () => void
  hasIconChange?: boolean
  value?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onClose?: (event: FocusEvent<any>) => void
  hasSearchTextStored?: boolean
}

export const SearchInputWithDebounce: React.FC<SearchInputWithDebounceProps> = (props) => {
  const [searchText, setSearchText] = useState('')
  const onDebounceRef = useRef(props.onDebounce)
  onDebounceRef.current = props.onDebounce

  // Keep a stable debounce instance. Recreating it when `onDebounce` identity
  // changes used to clear pending searches, so typing never reached the API
  // when the parent re-rendered (GR-3083 / GR-3084).
  const debounceSearch = useMemo(() => {
    return debounce((newSearchText: string) => {
      onDebounceRef.current(newSearchText)
    }, 200)
  }, [])

  const handleGameSearchTermChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchText = event.target.value
      setSearchText(newSearchText)
      debounceSearch(newSearchText)
    },
    [debounceSearch]
  )

  useEffect(() => {
    return () => {
      debounceSearch.clear()
    }
  }, [debounceSearch])

  const onFocus = (event: FocusEvent<any>) => {
    if (props.onFocus) {
      props.onFocus(event)
    }
  }

  const onBlur = (event: FocusEvent<any>) => {
    if (props.onBlur) {
      props.onBlur(event)
    }
  }

  return (
    <>
      <TextField
        autoComplete="off"
        className="SearchInputWithDebounce"
        size="small"
        fullWidth
        label={
          <div>
            <Search style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '5px', marginTop: '-2px' }} />
            <span>{props.fieldText}</span>
          </div>
        }
        variant="outlined"
        color="primary"
        value={searchText}
        onChange={handleGameSearchTermChange}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {props.hasIconChange ? (
        searchText.length ? (
          <IconButton
            className="GameSearchInput__advanced-search"
            color="primary"
            onClick={() => {
              setSearchText('')
              onDebounceRef.current('')
            }}
          >
            <Close />
          </IconButton>
        ) : (
          // can make this component more generic with children prop and pass the icon as children from parent
          <IconButton className="GameSearchInput__advanced-search" color="primary" onClick={props.onOpenGameSearchDialog}>
            <OpenInNew />
          </IconButton>
        )
      ) : null}
    </>
  )
}
